import { useReducer, useCallback, useEffect } from 'react';

/**
 * POS state management.
 * Standalone from checkout - manages cart items, totals, discounts, tips, screens.
 */

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  SET_QUANTITY: 'SET_QUANTITY',
  UPDATE_ITEM: 'UPDATE_ITEM',
  UPDATE_PRICE: 'UPDATE_PRICE',
  SET_DISCOUNT: 'SET_DISCOUNT',
  CLEAR_DISCOUNT: 'CLEAR_DISCOUNT',
  SET_TIP: 'SET_TIP',
  SET_TAX: 'SET_TAX',
  SET_CLIENT: 'SET_CLIENT',
  SET_LOYALTY_REWARDS: 'SET_LOYALTY_REWARDS',
  ADD_TRANSACTION: 'ADD_TRANSACTION',
  REMOVE_TRANSACTION: 'REMOVE_TRANSACTION',
  SET_NOTE: 'SET_NOTE',
  CLEAR_CART: 'CLEAR_CART',
  RESTORE_CART: 'RESTORE_CART',
  MARK_COMPLETE: 'MARK_COMPLETE',
  RESET: 'RESET',
  SET_SCREEN: 'SET_SCREEN',
  SET_AMOUNT_MODE: 'SET_AMOUNT_MODE',
  SET_CUSTOM_AMOUNT: 'SET_CUSTOM_AMOUNT',
  SET_STAFF: 'SET_STAFF',
  SET_PROCESSING: 'SET_PROCESSING',
  SET_PAYMENT_LINK_DATA: 'SET_PAYMENT_LINK_DATA',
  SET_BOOKING_ID: 'SET_BOOKING_ID',
  APPLY_PROMO: 'APPLY_PROMO',
  REMOVE_PROMO: 'REMOVE_PROMO',
};

const initialState = {
  items: [],
  isProcessingPayment: false,
  paymentLinkData: null,
  paymentLinkType: null,
  bookingId: null,
  client: null,
  loyaltyRewards: [], // the picked client's redeemable cards at this salon (rewardsAvailable > 0)
  discount: null, // { type: 'percentage'|'fixed', value }
  // A discount code: its saving sits on the lines it covers as promoOff (per
  // unit). One discount per sale, so a code and `discount` never sit together.
  promo: null,
  promoDropped: null,
  tip: { amount: 0, type: null },
  transactions: [],
  note: '',
  isComplete: false,

  // Screen navigation
  screen: 'summary', // summary | payment | tip | discount | complete
  amountMode: 'balance', // balance | custom
  customAmount: '',

  // Derived (recalculated)
  subtotal: 0,
  discountAmount: 0,
  // Tax added ON TOP of the balance (exclusive mode only; 0 when inclusive/off).
  taxAmount: 0,
  tipAmount: 0,
  total: 0,
  totalPaid: 0,
  balanceRemaining: 0,
};

function recalc(state) {
  const subtotal = round2(state.items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0));

  // A code's saving, line by line
  let discountAmount = round2(state.items.reduce((sum, item) => sum + (item.promoOff || 0) * (item.quantity || 1), 0));
  if (state.discount) {
    if (state.discount.type === 'percentage') {
      discountAmount = round2(discountAmount + subtotal * (state.discount.value / 100));
    } else if (state.discount.type === 'fixed') {
      discountAmount = round2(discountAmount + Math.min(state.discount.value, subtotal));
    }
  }

  const tipAmount = round2(state.tip?.amount || 0);
  // Tip is recorded separately (cash/card) and doesn't affect the balance to collect
  const total = round2(Math.max(0, subtotal - discountAmount + (state.taxAmount || 0)));
  const totalPaid = round2(state.transactions.reduce((sum, t) => sum + (t.amount || 0), 0));
  const balanceRemaining = round2(Math.max(0, total - totalPaid));

  return { ...state, subtotal, discountAmount, tipAmount, total, totalPaid, balanceRemaining };
}

const stripPromo = (item) => {
  if (item.promoOff == null) return item;
  const { promoOff, ...rest } = item; // eslint-disable-line no-unused-vars
  return rest;
};

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD_ITEM:
      return recalc({ ...state, items: [...state.items, action.payload] });

    case ACTIONS.REMOVE_ITEM:
      return recalc({ ...state, items: state.items.filter((_, i) => i !== action.payload) });

    // Patch fields on one line — used when a variant is chosen in the sidebar,
    // which can also change the line's price (a variant may override it).
    case ACTIONS.UPDATE_ITEM: {
      const { index, patch } = action.payload;
      return recalc({
        ...state,
        items: state.items.map((item, i) => i === index ? { ...item, ...patch } : item),
      });
    }

    case ACTIONS.SET_QUANTITY: {
      const { index, quantity } = action.payload;
      const q = Math.max(1, Math.round(quantity));
      return recalc({
        ...state,
        items: state.items.map((item, i) => i === index ? { ...item, quantity: q } : item),
      });
    }

    case ACTIONS.UPDATE_PRICE: {
      const { index, price } = action.payload;
      return recalc({
        ...state,
        items: state.items.map((item, i) => i === index ? { ...item, listPrice: item.listPrice != null ? item.listPrice : item.price, price: round2(parseFloat(price) || 0), priceEdited: true } : item),
      });
    }

    // One discount per sale: a manual discount replaces a code
    case ACTIONS.SET_DISCOUNT:
      return recalc({ ...state, discount: action.payload, promo: null, items: state.items.map(stripPromo) });

    case ACTIONS.CLEAR_DISCOUNT:
      return recalc({ ...state, discount: null, promo: null, items: state.items.map(stripPromo) });

    case ACTIONS.APPLY_PROMO: {
      const { promo, offs } = action.payload;
      return recalc({
        ...state,
        discount: null,
        promo,
        promoDropped: null,
        items: state.items.map((item, i) => (offs[i] ? { ...item, promoOff: offs[i] } : stripPromo(item))),
      });
    }

    case ACTIONS.REMOVE_PROMO:
      return recalc({ ...state, promo: null, promoDropped: null, items: state.items.map(stripPromo) });

    case ACTIONS.SET_TIP:
      return recalc({ ...state, tip: action.payload });

    case ACTIONS.SET_TAX:
      return recalc({ ...state, taxAmount: round2(action.payload || 0) });

    case ACTIONS.SET_CLIENT:
      // New client → drop any stale rewards until reloaded for them.
      return { ...state, client: action.payload, loyaltyRewards: [] };

    case ACTIONS.SET_LOYALTY_REWARDS:
      return { ...state, loyaltyRewards: action.payload || [] };

    case ACTIONS.ADD_TRANSACTION: {
      const next = recalc({ ...state, transactions: [...state.transactions, action.payload] });
      // Always return to summary for review. Tip is optional via More Options.
      return { ...next, screen: 'summary' };
    }

    case ACTIONS.REMOVE_TRANSACTION:
      return recalc({ ...state, transactions: state.transactions.filter((_, i) => i !== action.payload) });

    case ACTIONS.SET_NOTE:
      return { ...state, note: action.payload };

    case ACTIONS.SET_STAFF: {
      const { staffId, staffName } = action.payload;
      return {
        ...state,
        items: state.items.map(item => ({ ...item, staffId, staffName })),
      };
    }

    case ACTIONS.CLEAR_CART:
      return recalc({ ...initialState });

    case ACTIONS.RESTORE_CART:
      return recalc(action.payload);

    case ACTIONS.MARK_COMPLETE:
      return { ...state, isComplete: true, screen: 'complete' };

    case ACTIONS.RESET:
      return { ...initialState };

    case ACTIONS.SET_SCREEN:
      return { ...state, screen: action.payload };

    case ACTIONS.SET_AMOUNT_MODE:
      return { ...state, amountMode: action.payload };

    case ACTIONS.SET_CUSTOM_AMOUNT:
      return { ...state, customAmount: action.payload };

    case ACTIONS.SET_PROCESSING:
      return { ...state, isProcessingPayment: action.payload };

    case ACTIONS.SET_PAYMENT_LINK_DATA:
      return { ...state, paymentLinkData: action.payload?.data || null, paymentLinkType: action.payload?.type || null };

    case ACTIONS.SET_BOOKING_ID:
      return { ...state, bookingId: action.payload };

    default:
      return state;
  }
}

const STORAGE_KEY = 'beu_pos_cart';

// Try to restore cart from localStorage
function getInitialState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Only restore if there are items (don't restore empty/completed carts)
      if (parsed.items && parsed.items.length > 0) {
        // If there's a pending payment link, go straight to that screen
        const screen = parsed.paymentLinkData ? 'paymentLink' : 'summary';
        return recalc({ ...initialState, ...parsed, screen, isComplete: false, isProcessingPayment: false });
      }
    }
  } catch (e) { /* ignore */ }
  return initialState;
}

// A code's saving was worked out for the cart as it was: changing the cart
// takes it off, and the cart says so (it can go back on)
const CART_CHANGES = [ACTIONS.ADD_ITEM, ACTIONS.REMOVE_ITEM, ACTIONS.UPDATE_ITEM, ACTIONS.SET_QUANTITY, ACTIONS.UPDATE_PRICE, ACTIONS.SET_STAFF];
export function posReducer(state, action) {
  const next = reducer(state, action);
  if (!state.promo || !CART_CHANGES.includes(action.type)) return next;
  return recalc({ ...next, promo: null, promoDropped: state.promo.code, items: next.items.map(stripPromo) });
}

export default function usePOSState() {
  const [state, dispatch] = useReducer(posReducer, null, getInitialState);

  // Auto-save cart to localStorage on every state change
  useEffect(() => {
    if (state.items.length > 0 || state.paymentLinkData) {
      const toSave = {
        items: state.items,
        client: state.client,
        discount: state.discount,
        promo: state.promo,
        tip: state.tip,
        transactions: state.transactions,
        paymentLinkData: state.paymentLinkData,
        paymentLinkType: state.paymentLinkType,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [state.items, state.client, state.discount, state.promo, state.tip, state.transactions, state.paymentLinkData]);

  const actions = {
    addItem: useCallback((item) => dispatch({ type: ACTIONS.ADD_ITEM, payload: item }), []),
    removeItem: useCallback((i) => dispatch({ type: ACTIONS.REMOVE_ITEM, payload: i }), []),
    setQuantity: useCallback((index, quantity) => dispatch({ type: ACTIONS.SET_QUANTITY, payload: { index, quantity } }), []),
    updateItem: useCallback((index, patch) => dispatch({ type: ACTIONS.UPDATE_ITEM, payload: { index, patch } }), []),
    updatePrice: useCallback((index, price) => dispatch({ type: ACTIONS.UPDATE_PRICE, payload: { index, price } }), []),
    setDiscount: useCallback((d) => dispatch({ type: ACTIONS.SET_DISCOUNT, payload: d }), []),
    clearDiscount: useCallback(() => dispatch({ type: ACTIONS.CLEAR_DISCOUNT }), []),
    applyPromo: useCallback((promo, offs) => dispatch({ type: ACTIONS.APPLY_PROMO, payload: { promo, offs } }), []),
    removePromo: useCallback(() => dispatch({ type: ACTIONS.REMOVE_PROMO }), []),
    setTip: useCallback((t) => dispatch({ type: ACTIONS.SET_TIP, payload: t }), []),
    setTax: useCallback((amount) => dispatch({ type: ACTIONS.SET_TAX, payload: amount }), []),
    setClient: useCallback((c) => dispatch({ type: ACTIONS.SET_CLIENT, payload: c }), []),
    setLoyaltyRewards: useCallback((r) => dispatch({ type: ACTIONS.SET_LOYALTY_REWARDS, payload: r }), []),
    addTransaction: useCallback((t) => dispatch({ type: ACTIONS.ADD_TRANSACTION, payload: t }), []),
    removeTransaction: useCallback((i) => dispatch({ type: ACTIONS.REMOVE_TRANSACTION, payload: i }), []),
    setNote: useCallback((n) => dispatch({ type: ACTIONS.SET_NOTE, payload: n }), []),
    clearCart: useCallback(() => dispatch({ type: ACTIONS.CLEAR_CART }), []),
    restoreCart: useCallback((data) => dispatch({ type: ACTIONS.RESTORE_CART, payload: data }), []),
    markComplete: useCallback(() => dispatch({ type: ACTIONS.MARK_COMPLETE }), []),
    reset: useCallback(() => dispatch({ type: ACTIONS.RESET }), []),
    setScreen: useCallback((s) => dispatch({ type: ACTIONS.SET_SCREEN, payload: s }), []),
    setAmountMode: useCallback((m) => dispatch({ type: ACTIONS.SET_AMOUNT_MODE, payload: m }), []),
    setCustomAmount: useCallback((a) => dispatch({ type: ACTIONS.SET_CUSTOM_AMOUNT, payload: a }), []),
    setStaff: useCallback((s) => dispatch({ type: ACTIONS.SET_STAFF, payload: s }), []),
    setProcessing: useCallback((v) => dispatch({ type: ACTIONS.SET_PROCESSING, payload: v }), []),
    setPaymentLinkData: useCallback((data, type) => dispatch({ type: ACTIONS.SET_PAYMENT_LINK_DATA, payload: { data, type } }), []),
    setBookingId: useCallback((id) => dispatch({ type: ACTIONS.SET_BOOKING_ID, payload: id }), []),
  };

  return [state, actions];
}

export { ACTIONS, initialState as posInitialState, recalc as posRecalc };
